"use client";

import { useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/context/auth-context";

const SKIP_PATHS = [
  "/onboarding",
  "/login",
  "/register",
  "/signup",
  "/founding-beta",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/tutor/apply",
  "/api",
];

/**
 * Redirects newly logged-in STUDENT users to /onboarding if they haven't
 * completed it yet. Tutors are excluded — they have their own TutorRedirectGuard
 * and /tutor/onboarding flow. Completion state is persisted in the DB
 * (User.onboardingCompleted) and surfaced via the session. Falls back to a
 * single preferences API check on the very first render when the session token
 * is stale.
 */
export function OnboardingGuard() {
  const { user, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const checked = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (typeof document !== "undefined" && document.documentElement.classList.contains("native-embed")) return;
    if (SKIP_PATHS.some((p) => pathname.startsWith(p))) return;
    if (checked.current) return;

    // Tutors have their own TutorRedirectGuard — skip this guard entirely for them
    const role = (user as any).role?.toUpperCase?.() ?? "";
    if (role.includes("TUTOR") || role.includes("ADMIN") || role.includes("ADVERTISER") || role.includes("EMPLOYEE")) return;

    checked.current = true;

    // If the session already has the flag, use it directly
    const sessionCompleted = (user as any).onboardingCompleted;
    if (sessionCompleted === true) return;

    // Session may be stale (first login before flag was added) — confirm via API
    fetch("/api/user/preferences")
      .then((r) => r.json())
      .then((data) => {
        if (!data.onboardingCompleted) {
          router.push("/onboarding");
        }
      })
      .catch(() => {});
  }, [isAuthenticated, user, pathname, router]);

  return null;
}
